import { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { SvgIcons } from '../components/SvgIcons';
import { TenantContext } from '../context/TenantContext';
import { getTenantFromSubdomain, isAdminHost } from '../utils/tenant';
import { getTenantLabels } from '../utils/tenantTerms';

const Register = () => {
  const { tenantType } = useContext(TenantContext);
  const labels = getTenantLabels(tenantType);
  const isUniversityTenant = tenantType === 'university';

  const [tenantInfo, setTenantInfo] = useState(null);
  const [fetchingTenant, setFetchingTenant] = useState(false);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'examiner',
    university_id: '',
    college_id: '',
    department_id: '',
    subject_id: '',
    qualification: '',
    years_of_experience: '',
    institution_name: '',
    specialization: ''
  });

  const [universities, setUniversities] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [, setSubjects] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const primaryColor = tenantInfo?.primary_color || '#0F172A';

  // Load tenant branding
  useEffect(() => {
    const fetchTenantDetails = async () => {
      const slug = getTenantFromSubdomain();
      if (!slug) return;
      setFetchingTenant(true);
      try {
        const res = await API.get(`/public/tenant-details?slug=${slug}`);
        if (res.data.success) {
          setTenantInfo(res.data.tenant);
        }
      } catch (err) {
        console.error('Failed to load tenant details:', err);
      } finally {
        setFetchingTenant(false);
      }
    };
    fetchTenantDetails();
  }, []);

  const getLogoSrc = (logoUrl) => {
    const host = process.env.REACT_APP_DEV_API_URL || 'http://localhost:5000';
    return `${host}${logoUrl}`;
  };

  useEffect(() => {
    if (isAdminHost()) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    fetchUniversities();
  }, []);

  useEffect(() => {
    if (formData.university_id) {
      fetchColleges(formData.university_id);
    }
  }, [formData.university_id]);

  useEffect(() => {
    const collegeRoles = ['college_admin', 'hod', 'examiner', 'subject_matter_expert', 'moderator'];
    if (!isUniversityTenant && collegeRoles.includes(formData.role)) {
      fetchAllColleges();
    }
  }, [formData.role, isUniversityTenant]);

  useEffect(() => {
    if (formData.college_id) {
      fetchDepartments(formData.college_id);
    }
  }, [formData.college_id]);

  const needsSubject = useCallback(() => {
    // Subject assignment removed from registration - will be assigned after user creation
    return false;
  }, []);

  useEffect(() => {
    if (formData.department_id && needsSubject()) {
      fetchSubjects(formData.department_id);
    }
  }, [formData.department_id, needsSubject]);

  const fetchUniversities = async () => {
    try {
      const { data } = await API.get('/public/universities');
      setUniversities(data.universities);
    } catch (err) {
      console.error('Failed to fetch universities');
    }
  };

  const fetchColleges = async (universityId) => {
    try {
      const { data } = await API.get(`/public/colleges?university_id=${universityId}`);
      setColleges(data.colleges);
    } catch (err) {
      console.error('Failed to fetch colleges');
    }
  };

  const fetchAllColleges = async () => {
    try {
      const { data } = await API.get('/public/colleges');
      setColleges(data.colleges);
    } catch (err) {
      console.error('Failed to fetch colleges');
    }
  };

  const fetchDepartments = async (collegeId) => {
    try {
      const { data } = await API.get(`/public/departments?college_id=${collegeId}`);
      setDepartments(data.departments);
    } catch (err) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchSubjects = async (departmentId) => {
    try {
      const { data } = await API.get(`/public/subjects?department_id=${departmentId}`);
      setSubjects(data.subjects);
    } catch (err) {
      console.error('Failed to fetch subjects');
    }
  };

  const needsDepartment = () => {
    return ['hod', 'examiner', 'subject_matter_expert', 'moderator'].includes(formData.role);
  };

  const needsCollege = () => {
    return ['college_admin', 'hod', 'examiner', 'subject_matter_expert', 'moderator'].includes(formData.role);
  };

  const needsUniversity = () => {
    if (!isUniversityTenant) return false;
    return ['university_admin', 'coe', 'college_admin', 'hod', 'examiner', 'subject_matter_expert', 'moderator'].includes(formData.role);
  };

  // University tenants register users into their own single university — lock the
  // university field to that university instead of presenting a free choice.
  const universityLocked = isUniversityTenant && universities.length === 1;
  // Non-university tenants (college/school/etc.) belong to a single college — lock
  // the college field to that college.
  const collegeLocked = !isUniversityTenant && colleges.length === 1;

  useEffect(() => {
    if (universityLocked) {
      const onlyId = String(universities[0].university_id);
      setFormData(prev => (prev.university_id === onlyId ? prev : { ...prev, university_id: onlyId }));
    }
  }, [universityLocked, universities]);

  useEffect(() => {
    const collegeRoles = ['college_admin', 'hod', 'examiner', 'subject_matter_expert', 'moderator'];
    if (collegeLocked && collegeRoles.includes(formData.role)) {
      const onlyId = String(colleges[0].college_id);
      setFormData(prev => (prev.college_id === onlyId ? prev : { ...prev, college_id: onlyId }));
    }
  }, [collegeLocked, colleges, formData.role]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role') {
      setFormData({
        ...formData,
        role: value,
        university_id: '',
        college_id: '',
        department_id: '',
        subject_id: ''
      });
      setColleges([]);
      setDepartments([]);
      setSubjects([]);
    } else if (name === 'university_id') {
      setFormData({
        ...formData,
        university_id: value,
        college_id: '',
        department_id: '',
        subject_id: ''
      });
      setDepartments([]);
      setSubjects([]);
    } else if (name === 'college_id') {
      setFormData({
        ...formData,
        college_id: value,
        department_id: '',
        subject_id: ''
      });
      setSubjects([]);
    } else if (name === 'department_id') {
      setFormData({
        ...formData,
        department_id: value,
        subject_id: ''
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const nextStep = () => {
    setError('');

    if (step === 1) {
      if (!formData.role) {
        setError('Please select a role');
        return;
      }
    } else if (step === 2) {
      if (!formData.name || !formData.email || !formData.password || !formData.phone) {
        setError('Please fill in all required fields');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
      if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        setError('Please enter a valid phone number (10 digits)');
        return;
      }
    } else if (step === 3) {
      if (needsUniversity() && !formData.university_id) {
        setError('Please select a university');
        return;
      }
      if (needsCollege() && !formData.college_id) {
        setError('Please select a college');
        return;
      }
      if (needsDepartment() && !formData.department_id) {
        setError('Please select a department');
        return;
      }
      // Subject selection removed from registration validation
    }

    setStep(step + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data } = await API.post('/auth/register', formData);

      if (data.user?.status === 'pending') {
        let approvalMsg = 'Registration submitted successfully! ';
        if (formData.role === 'university_admin') {
          approvalMsg += 'Your account is pending Super Admin approval.';
        } else if (formData.role === 'coe') {
          approvalMsg += 'Your account is pending University Admin approval.';
        } else if (formData.role === 'college_admin') {
          approvalMsg += isUniversityTenant
            ? 'Your account is pending University Admin approval.'
            : 'Your account is pending Super Admin approval.';
        } else if (formData.role === 'hod') {
          approvalMsg += 'Your account is pending College Admin approval.';
        } else if (formData.role === 'moderator') {
          approvalMsg += 'Your account is pending HOD approval.';
        } else if (formData.role === 'subject_matter_expert') {
          approvalMsg += 'Your account is pending Moderator approval.';
        } else if (formData.role === 'examiner') {
          approvalMsg += 'Your account is pending Subject Matter Expert approval.';
        }
        setSuccess(approvalMsg);
        setTimeout(() => navigate('/login'), 4000);
      } else {
        setSuccess('Registration successful!');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator-container">
      <div className="step-indicator">
        {[1, 2, 3, 4].map((s, index) => (
          <div key={s} className="step-item">
            <div
              className={`step-circle ${step >= s ? 'completed' : ''} ${step === s ? 'active' : ''}`}
            >
              {step > s ? '✓' : s}
            </div>
            {index < 3 && (
              <div className={`step-line ${step > s ? 'completed' : ''}`}></div>
            )}
          </div>
        ))}
      </div>
      <div className="step-labels">
        <span className={step >= 1 ? 'active-label' : ''}>Role</span>
        <span className={step >= 2 ? 'active-label' : ''}>Personal</span>
        <span className={step >= 3 ? 'active-label' : ''}>Institution</span>
        <span className={step >= 4 ? 'active-label' : ''}>Complete</span>
      </div>
    </div>
  );

  if (fetchingTenant) {
    return (
      <div className="auth-split">
        <div className="auth-brand" style={{ backgroundColor: primaryColor }}>
          <div className="auth-brand-overlay" />
          <div className="auth-brand-content">
            <div className="spinner" style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#FFFFFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        </div>
        <div className="auth-console">
          <div className="auth-console-inner">
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Resolving workspace environment...</p>
          </div>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="auth-split">
      {/* Left Panel — Dynamic Tenant Brand Block */}
      <div className="auth-brand" style={{ backgroundColor: primaryColor }}>
        <div className="auth-brand-overlay" />
        <div className="auth-brand-content">
          {tenantInfo ? (
            <>
              {tenantInfo.logo_url ? (
                <img
                  src={getLogoSrc(tenantInfo.logo_url)}
                  alt={tenantInfo.university_name}
                  className="auth-brand-logo"
                />
              ) : (
                <div className="auth-brand-logo-placeholder">
                  {tenantInfo.university_name.charAt(0).toUpperCase()}
                </div>
              )}
              <h2 className="auth-brand-name">{tenantInfo.university_name}</h2>
              {labels && (
                <span className="auth-brand-label">{labels.tenantLabel}</span>
              )}
              <div className="auth-brand-sub">
                <p className="auth-brand-sub-text">Secure Examination Infrastructure Portal</p>
                <p className="auth-brand-sub-note">Authorized Access Only. System sessions are ledger-audited.</p>
              </div>
            </>
          ) : (
            <>
              <div className="auth-brand-logo-placeholder" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#FFFFFF', width: 64, height: 64, fontSize: '1.75rem' }}>
                Q
              </div>
              <h2 className="auth-brand-name">Unified QPG</h2>
            </>
          )}
        </div>
      </div>

      {/* Right Panel — Secure Interactive Console */}
      <div className="auth-console" style={{ '--tenant-primary': primaryColor }}>
        <div className="auth-console-inner">
          {!tenantInfo && (
            <div className="auth-console-header">
              <h1 className="auth-title">Registration</h1>
              <p className="auth-subtitle">
                {step === 1 && 'Step 1: Choose Your Role'}
                {step === 2 && 'Step 2: Personal Information'}
                {step === 3 && 'Step 3: Institution Details'}
                {step === 4 && 'Step 4: Professional Profile'}
              </p>
            </div>
          )}

          {(!tenantInfo || step === 1) && renderStepIndicator()}

          {error && <div className="form-error">{error}</div>}
          {success && <div className="form-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Role Selection */}
            {step === 1 && (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div className="form-group">
                  <label className="form-label">Select Your Role</label>
                  <div className="role-grid-compact" style={{ marginTop: '1rem' }}>
                    {[
                      ...(isUniversityTenant
                        ? [{ value: 'university_admin', label: 'University Admin', IconComponent: SvgIcons.UniversityAdmin },
                           { value: 'coe', label: 'Controller of Examinations', IconComponent: SvgIcons.COE }]
                        : []),
                      { value: 'college_admin', label: 'College Admin', IconComponent: SvgIcons.CollegeAdmin },
                      { value: 'hod', label: 'Head of Department', IconComponent: SvgIcons.HOD },
                      { value: 'moderator', label: 'Moderator', IconComponent: SvgIcons.Moderator },
                      { value: 'subject_matter_expert', label: 'Subject Matter Expert', IconComponent: SvgIcons.SME },
                      { value: 'examiner', label: 'Examiner', IconComponent: SvgIcons.Examiner }
                    ].map((role) => (
                      <div
                        key={role.value}
                        onClick={() => setFormData({ ...formData, role: role.value })}
                        className={`role-card-compact ${formData.role === role.value ? 'selected' : ''}`}
                      >
                        <div className="role-icon">
                          <role.IconComponent width={28} height={28} />
                        </div>
                        <div className="role-label">{role.label}</div>
                        {formData.role === role.value && (
                          <div className="role-check">
                            <SvgIcons.Check width={16} height={16} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Personal Info */}
            {step === 2 && (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        className="form-input"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Minimum 6 characters"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your 10-digit phone number"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Institution Details */}
            {step === 3 && (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div className="form-row">
                  {needsUniversity() && (
                    <div className="form-group">
                      <label className="form-label">University *</label>
                      <select
                        name="university_id"
                        className="form-select"
                        value={formData.university_id}
                        onChange={handleChange}
                        required
                        disabled={universityLocked}
                      >
                        <option value="">Select University</option>
                        {universities.map(uni => (
                          <option key={uni.university_id} value={uni.university_id}>
                            {uni.university_name}
                          </option>
                        ))}
                      </select>
                      {universityLocked && (
                        <small style={{ color: '#475569', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                          Locked to your institution
                        </small>
                      )}
                    </div>
                  )}

                  {needsCollege() && (
                    <div className="form-group">
                      <label className="form-label">College *</label>
                      <select
                        name="college_id"
                        className="form-select"
                        value={formData.college_id}
                        onChange={handleChange}
                        required
                        disabled={collegeLocked || (isUniversityTenant && !formData.university_id)}
                      >
                        <option value="">Select College</option>
                        {colleges.map(college => (
                          <option key={college.college_id} value={college.college_id}>
                            {college.college_name}
                          </option>
                        ))}
                      </select>
                      {collegeLocked && (
                        <small style={{ color: '#475569', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                          Locked to your institution
                        </small>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-row">
                  {needsDepartment() && (
                    <div className="form-group">
                      <label className="form-label">Department *</label>
                      <select
                        name="department_id"
                        className="form-select"
                        value={formData.department_id}
                        onChange={handleChange}
                        required
                        disabled={!formData.college_id}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option
                            key={dept.department_id}
                            value={dept.department_id}
                            disabled={formData.role === 'hod' && dept.hod_user_id}
                          >
                            {dept.department_name} {dept.department_code && `(${dept.department_code})`}
                            {formData.role === 'hod' && dept.hod_user_id && ' - HOD Already Assigned'}
                          </option>
                        ))}
                      </select>
                      {formData.role === 'hod' && (
                        <small style={{ color: '#475569', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                          Only departments without an existing HOD are available
                        </small>
                      )}
                    </div>
                  )}

                  {/* Subject selection removed from registration - will be assigned after user creation */}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1.5rem'
            }}>
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-secondary"
                  style={{ flex: '1' }}
                >
                  Back
                </button>
              )}
              {step < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                  style={{ flex: '1' }}
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: '1' }}
                  disabled={loading}
                >
                  {loading ? 'Registering...' : 'Complete Registration'}
                </button>
              )}
            </div>
          </form>

          <div className="auth-link" style={{ marginTop: '1.5rem' }}>
            Already have an account? <Link to="/login">Sign In</Link>
          </div>

          {step === 4 && (
            <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#475569', textAlign: 'center', padding: '0.75rem', background: '#F8FAFC', borderRadius: '0.5rem', border: '1px solid #E2E8F0' }}>
              <strong>Approval Hierarchy:</strong><br />
              {isUniversityTenant
                ? 'Super Admin → University Admin → College Admin → HOD → Moderator → SME → Examiner'
                : 'Super Admin → College Admin → HOD → Moderator → SME → Examiner'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
